import { AlertTriangle } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ExampleCard } from '@/components/editor/example-card'
import { SourceAttribution } from '@/components/editor/source-attribution'
import { Copyright } from '@/components/ui/copyright'
import { Image } from '@/components/ui/image'
import { SyntaxHighlightedMarkdown } from '@/components/ui/syntax-highlighted-markdown'
import {
  buildExamplesFromTestConfig,
  type ExampleError,
} from '@/lib/build-examples'
import { buildAttribution, copyrightScopeFor } from '@/lib/problems/attribution'
import type { ProblemSource } from '../types'
import { toCandidates } from './source-candidates'
import type { TestConfiguration } from './tests-section'

interface StatementPreviewProps {
  title: string
  statement: string
  testConfig: TestConfiguration
  constraints?: { content: string }[]
  /** The editor's current sources, saved or not. */
  sources?: ProblemSource[]
}

/**
 * Convert <Image src="..." alt="..." /> JSX tags to standard markdown image
 * syntax so react-markdown can render them. The img component override below
 * then renders them using our Image component with Vercel optimization.
 */
function preprocessImageTags(markdown: string): string {
  return markdown.replace(/<Image\s+([^>]*?)\s*\/?>/gi, (match, attrs) => {
    const srcMatch = /src=["']([^"']+)["']/.exec(attrs)
    const altMatch = /alt=["']([^"']*?)["']/.exec(attrs)
    if (srcMatch) {
      return `![${altMatch?.[1] || ''}](${srcMatch[1]})`
    }
    return match
  })
}

/**
 * Custom components for SyntaxHighlightedMarkdown to handle Image rendering.
 * Maps standard <img> elements to our Image component with Vercel optimization.
 */
const markdownComponents = {
  img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <Image src={src || ''} alt={alt} />
  ),
}

function ExampleErrorCard({ error }: { error: ExampleError }) {
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
      <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>
          Could not render Example {error.index}: {error.message}
        </span>
      </div>
    </div>
  )
}

/**
 * Error boundary that wraps the entire preview to catch unexpected render errors.
 */
class PreviewErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Statement preview error:', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg border border-red-300 bg-red-50 p-6 dark:border-red-700 dark:bg-red-950">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium">Preview failed to render</span>
          </div>
          <pre className="mt-2 text-xs text-red-700 dark:text-red-300">
            {this.state.error.message}
          </pre>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * Renders a 1:1 preview of the student-facing DescriptionPane using data
 * from the admin problem editor. Reuses the same components (ExampleCard,
 * SyntaxHighlightedMarkdown, Image) with identical styling.
 */
export function StatementPreview({
  title,
  statement,
  testConfig,
  constraints,
  sources = [],
}: StatementPreviewProps) {
  const { examples, errors } = buildExamplesFromTestConfig(testConfig)
  const processedStatement = preprocessImageTags(statement)
  const attribution = buildAttribution(
    toCandidates(sources),
    title || 'Untitled Problem',
  )

  return (
    <PreviewErrorBoundary>
      <div className="h-full overflow-auto">
        <div className="p-6">
          {/* Problem Title — matches DescriptionPane */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {title || 'Untitled Problem'}
            </h1>
            <SourceAttribution attribution={attribution} />
          </div>

          {/* Problem Statement — matches DescriptionPane prose styling */}
          <section className="mb-6">
            {processedStatement ? (
              <SyntaxHighlightedMarkdown
                className="prose prose-sm dark:prose-invert max-w-none [&_code]:bg-gray-200 dark:[&_code]:bg-gray-700"
                components={markdownComponents}
              >
                {processedStatement}
              </SyntaxHighlightedMarkdown>
            ) : (
              <p className="text-muted-foreground text-sm italic">
                No statement content to preview.
              </p>
            )}
          </section>

          {/* Examples Section — matches DescriptionPane */}
          <section className="border-t border-gray-200 pt-6 dark:border-gray-700">
            {errors.map((error) => (
              <ExampleErrorCard key={`error-${error.index}`} error={error} />
            ))}
            {examples.length > 0 ? (
              examples.map((example) => (
                <ExampleCard key={example.index} example={example} />
              ))
            ) : errors.length === 0 ? (
              <p className="text-muted-foreground text-sm italic">
                No examples available. Mark test cases as "Show as example" in
                the Tests tab.
              </p>
            ) : null}
          </section>

          {/* Constraints Section */}
          {constraints && constraints.length > 0 && (
            <section className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <p className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
                Constraints:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                {constraints.map((c) => (
                  <li key={c.content}>
                    <SyntaxHighlightedMarkdown className="prose prose-sm dark:prose-invert inline max-w-none [&_code]:bg-gray-200 [&_p]:inline dark:[&_code]:bg-gray-700">
                      {c.content}
                    </SyntaxHighlightedMarkdown>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Copyright scope={copyrightScopeFor(attribution)} />
        </div>
      </div>
    </PreviewErrorBoundary>
  )
}
